import React, { useEffect, useState, useRef } from 'react'
import findIndex from 'lodash/findIndex'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useShowBackButton } from '../../hooks/useBackButton'
import usePageLoaded from '../../hooks/usePageLoaded'
import { RootState } from '../../store/rootReducer'

import {
    fetchContribution,
    copyContributionToSwiperList,
    fetchNextSlide,
    fetchPrevSlide,
    copyFirstSlideToBeginning,
    clearSwiperList
} from '../../store/contributions/contributionsSlice'
import {
    ContributionMaterialType,
    ContributionType,
    FetchIndexContributions
} from '../../store/contributions/contributionsTypings'

import SwiperCore, { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import Grid from '../../components/grid/grid'
import ContributionMaterial from '../../components/contribution/contributionMaterial'
import ContributionHeader from '../../components/contribution/contributionHeader'
import OriginalInfo from '../../components/contribution/originalInfo'
import NotFound from '../notFound/notFound'

import './contribution.sass'
import 'swiper/swiper.scss'
import 'swiper/components/navigation/navigation.scss'
import { useClearSwiperList } from '../../hooks/useSwiper'
import { disableLoader, enableLoader } from '../../store/common/commonSlice'
import { createLocalizedPath } from '../../utils/localizationHelpers'
import i18next, { getLangFromPath } from '../../i18next'
import { META } from '../../constants/meta'
import { clearCache } from '../../store/cacheSlise/cacheSlice'

SwiperCore.use([Navigation])

const Contribution = () => {
    usePageLoaded()
    useShowBackButton()
    // useSaveUrl()
    useClearSwiperList()

    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()

    const loading = useSelector<RootState, boolean>((state) => state.contributions.loading)
    const swiperList = useSelector<RootState, ContributionType[]>((state) => state.contributions.swiperList)
    const contribution = useSelector<RootState, ContributionType | null>((state) => state.contributions.contribution)
    const openedFromProfile = useSelector<RootState, string>((state) => state.contributions.openedFromProfile)
    const filters = useSelector<RootState, FetchIndexContributions>((state) => state.contributions.filters)
    const swiperListStopLoadNext = useSelector<RootState, boolean>(
        (state) => state.contributions.swiperListStopLoadNext
    )
    const swiperListStopLoadPrev = useSelector<RootState, boolean>(
        (state) => state.contributions.swiperListStopLoadPrev
    )
    const nextSlideLoading = useSelector<RootState, boolean>((state) => state.contributions.nextSlideLoading)
    const prevSlideLoading = useSelector<RootState, boolean>((state) => state.contributions.prevSlideLoading)
    const isLoaderDisabled = useSelector<RootState, boolean>((state) => state.common.isLoaderDisabled)
    const isAgeVerified = useSelector<RootState, boolean>((state) => state.common.isAgeVerified)

    const [swiperRef, setSwiperRef] = useState(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [tag] = useState(new URLSearchParams(location.search).get('tag'))

    const initId = useRef(contribution && contribution.id ? contribution.id : 0)
    const initIndex = useRef(Number(new URLSearchParams(location.search).get('index')))
    const rightArrowRef = useRef(null)
    const leftArrowRef = useRef(null)
    const [addedRightSlides, setAddedRightSlides] = useState(0)
    const [addedLeftSlides, setAddedLeftSlides] = useState(0)
    const [buttonsDisabled, setButtonsDisabled] = useState(false)

    //these functions are required for swiping when the user comes from the tag search page
    const getIdOfInitSlide = () =>
        swiperList.findIndex((item) => (contribution && contribution.id ? item.id === initId.current : 0))
    const getCurrentIndexOfPagination = () => initIndex.current - (getIdOfInitSlide() - currentIndex)
    const getLastIndexOfPagination = () => initIndex.current + addedRightSlides
    const getFirstIndexOfPagination = () => initIndex.current - addedLeftSlides

    const addNextSlide = () => {
        if (swiperList.length && !loading && contribution && contribution.id) {
            dispatch(
                fetchNextSlide({
                    ...filters,
                    id: String(swiperList[swiperList.length - 1].id),
                    context: openedFromProfile,
                    query: tag,
                    p: tag ? getLastIndexOfPagination() : null
                })
            )
            if (contribution && contribution.id) setAddedRightSlides(addedRightSlides + 1)
        }
    }

    const addPrevSlide = async () => {
        dispatch(copyFirstSlideToBeginning())
        if (swiperList.length && !loading && contribution && contribution.id) {
            await dispatch(
                fetchPrevSlide({
                    ...filters,
                    id: String(swiperList[0].id),
                    context: openedFromProfile,
                    query: tag,
                    p: tag ? getFirstIndexOfPagination() : null
                })
            )
            setAddedLeftSlides(addedLeftSlides + 1)
        }
        //🩼 prevents the slide from flashing
        if (swiperRef && !swiperListStopLoadPrev && contribution && contribution.id) swiperRef.slideNext(0)
    }

    const keyboardListeners = (event: KeyboardEvent) => {
        if (event.key === 'ArrowRight') rightArrowRef.current.click()
        if (event.key === 'ArrowLeft') leftArrowRef.current.click()
    }

    const isFirstLoading = useRef(true)

    useEffect(() => {
        if (isFirstLoading.current) {
            isFirstLoading.current = false
        } else {
            dispatch(clearCache())
            dispatch(fetchContribution({ number: contribution.number }))
            dispatch(clearSwiperList())
        }
    }, [i18next.language])

    useEffect(() => {
        window.addEventListener('keydown', keyboardListeners)
        return () => window.removeEventListener('keydown', keyboardListeners)
    }, [])

    useEffect(() => {
        if (swiperListStopLoadPrev && swiperRef && contribution && contribution.id)
            setTimeout(() => swiperRef.slidePrev(0), 0)
    }, [swiperListStopLoadPrev])

    const goNextIndex = () => {
        setCurrentIndex(currentIndex + 1)
    }

    const goPrevIndex = () => {
        setCurrentIndex(currentIndex - 1)
    }

    // <<< Preloading current and prev-next contributions
    useEffect(() => {
        if (contribution && contribution.id) {
            dispatch(copyContributionToSwiperList())
            setCurrentIndex(0)
        }
    }, [])

    useEffect(() => {
        const addSlides = async () => {
            if (swiperList.length === 1 && contribution && contribution.id) {
                await addNextSlide()
            }
            if (swiperList.length === 1 && !swiperListStopLoadPrev && contribution && contribution.id) {
                await addPrevSlide()
            }
        }
        if (contribution && contribution.id) addSlides()
        return () => {
            dispatch(enableLoader())
        }
    }, [swiperList])
    // >>>

    useEffect(() => {
        if (swiperList.length && contribution && contribution.id)
            history.replace({
                pathname: swiperList[currentIndex].urls[i18next.language] || swiperList[currentIndex].url,
                search: tag ? '?index=' + getCurrentIndexOfPagination() + '&tag=' + tag : ''
            })
    }, [currentIndex])

    return (
        <div className="pageContent" style={{ margin: '0 auto', maxWidth: '1360px', padding: '90px 27px' }}>
            <>
                <Helmet>
                    <title>{contribution.title}</title>
                    <meta
                        name="description"
                        content={
                            contribution.description_stripped ||
                            META.defaultUserContribution[getLangFromPath(location.pathname)].description.replace(
                                '{NICK}',
                                contribution.user.nick || 'Anonymous'
                            )
                        }
                    />
                    <link
                        rel="canonical"
                        href={process.env.CLIENT_PUBLIC_PATH.slice(0, -1) + location.pathname + location.search}
                    />
                </Helmet>
                <div className="contributionSwiperWrapper">
                    <Swiper
                        preventClicks={true}
                        onSwiper={setSwiperRef}
                        observer
                        spaceBetween={10}
                        centeredSlides={true}
                        longSwipes={false}
                        speed={300}
                        initialSlide={findIndex(swiperList, contribution)}
                        onBeforeTransitionStart={() => setButtonsDisabled(true)}
                        onSlideResetTransitionEnd={() => setButtonsDisabled(false)}
                        onSlideNextTransitionEnd={() => {
                            if (!swiperListStopLoadNext && currentIndex >= swiperList.length - 2) addNextSlide()
                            if (currentIndex !== swiperList.length - 1) goNextIndex()
                            setButtonsDisabled(false)
                        }}
                        onSlidePrevTransitionEnd={() => {
                            goPrevIndex()
                            if (currentIndex <= 1 && !swiperListStopLoadPrev) addPrevSlide()
                            setButtonsDisabled(false)
                        }}
                        onSlideChange={() => {
                            if (!isLoaderDisabled) dispatch(disableLoader())
                        }}
                    >
                        {swiperList.map((item) => (
                            <SwiperSlide key={item.id}>
                                <ContributionHeader contribution={item} />
                                <div
                                    className="contributionDescription"
                                    dangerouslySetInnerHTML={{ __html: item.description }}
                                />
                                <Grid
                                    items={item.materials.map((material: ContributionMaterialType) => (
                                        <ContributionMaterial
                                            material={material}
                                            isAgeVerified={isAgeVerified}
                                            pro={item.pro === 1}
                                            key={material.id}
                                        />
                                    ))}
                                />
                                <OriginalInfo data={item} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <div className="navigation">
                    <div className="prevContribution">
                        {prevSlideLoading && <div className="contributionLoader" />}
                        <a
                            href={
                                swiperList[currentIndex - 1]
                                    ? createLocalizedPath(swiperList[currentIndex - 1].url)
                                    : null
                            }
                            ref={leftArrowRef}
                            className={currentIndex > 0 ? 'prevIcon' : 'prevIcon lastContribution'}
                            onClick={(e) => {
                                e.preventDefault()
                                if (!buttonsDisabled) swiperRef.slidePrev()
                            }}
                        />
                    </div>
                    <div className="nextContribution">
                        {nextSlideLoading && <div className="contributionLoader" />}
                        <a
                            href={swiperList[currentIndex + 1] ? swiperList[currentIndex + 1].url : null}
                            ref={rightArrowRef}
                            className={
                                currentIndex !== swiperList.length - 1 ? 'nextIcon' : 'nextIcon lastContribution'
                            }
                            onClick={(e) => {
                                e.preventDefault()
                                if (!buttonsDisabled) swiperRef.slideNext()
                            }}
                        />
                    </div>
                </div>
            </>
        </div>
    )
}

// @ts-ignore
Contribution.getInitialProps = async ({ store, match }) => {
    const {
        params: { number }
    } = match
    try {
        await store.dispatch(fetchContribution({ number }))
    } catch (e) {
        return { statusCode: 404 }
    }
    const {
        contributions: { contribution }
    } = store.getState()
    if (!contribution) {
        return { statusCode: 404 }
    }
}

export default Contribution
